import { Component, ViewChild, ContentChildren, QueryList, TemplateRef, Input, Output, EventEmitter, ChangeDetectorRef } from "@angular/core";
import { PrimeTemplate } from "../../utils/shared";
import { MonacoCodeEditorComponent } from "../code-editor.component";
import { IClass, IMethod, ChangeInfo } from "../../code-editor-core/libs/interfaces/declaration";
import { IEditorOptions, IGlobalEditorOptions } from '../monaco.interface';
import { DesignElementLocaleHandler } from "../../utils/locale";
/** 工具栏按钮配置 */
export interface ToolButtonConfig {

  /** 按钮的标签文本 */
  label: string;

  /** 按钮的标题 */
  title?: string;

  /** 是否禁用，默认不禁用 */
  disabled?: boolean;

  /** 是否隐藏，默认不隐藏 */
  hide?: boolean;

  /** 点击事件处理函数 */
  handler?: (event: Event) => void;
}

@Component({
  selector: 'ide-code-editor',
  templateUrl: './ide-code-editor.html',
  styleUrls: ['./ide-code-editor.scss']
})
export class IdeCodeEditorComponent {

  /** 是否隐藏左侧的切换按钮 */
  @Input() hideSwitchButton: boolean = false;

  /** 切换到“设计器”按钮被点击 */
  @Output() switchView = new EventEmitter<void>(true);

  /** 右侧按钮工具栏配置 */
  @Input() toolBtnsConfig: ToolButtonConfig[] = [];

  /** 切换按钮的文本 */
  @Input() switchBtnText: string = '设计器';

  /** 切换按钮的icon图标的css类，为空则不显示图标 */
  @Input() switchBtnIconClass: string = '';

  @ViewChild("codeEditor") codeEditor: MonacoCodeEditorComponent;

  @ContentChildren(PrimeTemplate) templates: QueryList<any>;

  leftTemplate: TemplateRef<any>;

  centerTemplate: TemplateRef<any>;

  rightTemplate: TemplateRef<any>;

  appendTemplate: TemplateRef<any>;

  getLocaleVaue = DesignElementLocaleHandler.getValue
  constructor(private cd: ChangeDetectorRef) { }

  ngAfterContentInit() {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case 'left':
          this.leftTemplate = item.template;
          break;
        case 'center':
          this.centerTemplate = item.template;
          break;
        case 'right':
          this.rightTemplate = item.template;
          break;
        case 'append':
          this.appendTemplate = item.template;
          break;
        default:
          this.centerTemplate = item.template;
          break;
      }
    });
    this.cd.detectChanges();
  }

  /**
   * 打开文件
   * @param path - 文件路径
   * @param content - 文件内容
   */
  public async open(path: string, content?: string): Promise<void> {
    await this.codeEditor.open(path, content);
  }

  /**
   * 类新增方法片段
   * @param path 文件路径
   * @param method 方法结构描述
   * @param _class 类名，为空则默认第一个类
   */
  public async addMethod(path: string, method: IMethod, _class?: string): Promise<void> {
    await this.codeEditor.addMethod(path, method, _class);
  }

  /**
   * 获取文件解析结果
   * @param path - 文件路径
   * @param errorRecovery - 是否忽略语法错误并继续解析
   * @returns 文件内容及类结构，如果文件尚未打开则返回null
   */
  public async resolve(path: string, errorRecovery: boolean = false): Promise<{ content: string; hasFatalError?: boolean; classes?: IClass[]; }> {
    return await this.codeEditor.resolve(path, errorRecovery);
  }

  /**
   * 保存文件
   * @remarks
   * 将文件的changed属性设置为false，并且记录最后一次保存的文件内容，允许撤销修改
   * 如果文件尚未打开则静默失败
   * @param path - 文件路径
   * @param triggerChangedEvent - 是否触发内容变化事件（默认不触发，如果设置为true，则将触发Changed和OutlineChanged事件，当然，changed属性一定为false）
   */
  public async save(path: string, triggerChangedEvent: boolean = false): Promise<void> {
    await this.codeEditor.save(path, triggerChangedEvent);
  }

  /**
   * 关闭文件
   * @param path - 文件路径
   */
  public close(path: string): void {
    this.codeEditor.close(path);
  }

  /**
   * 监听内容变化事件
   * @remarks 注意：本方法的changeInfo中不会包含类结构信息，需要类结构信息需要订阅onOutlineChanged方法
   * @param fn 内容变化回调方法
   */
  public async onChanged(fn: (path: string, changeInfo: ChangeInfo) => void): Promise<void> {
    await this.codeEditor.onChanged(fn);
  }

  /**
   * 监听内容变化事件（该方法是经过防抖处理的）
   * @param fn 内容变化回调方法
   */
  public async onOutlineChanged(fn: (path: string, changeInfo: ChangeInfo) => void): Promise<void> {
    await this.codeEditor.onOutlineChanged(fn);
  }

  /**
   * 更改编辑器配置
   * @param path 文件路径
   * @param option 配置对象
   */
  public async updateOptions(path: string, newOptions: IEditorOptions & IGlobalEditorOptions): Promise<void> {
    await this.codeEditor.updateOptions(path, newOptions);
  }

  /**
   * 设置主题
   * @param isDarkTheme 是否深色主题
   */
  public async setTheme(isDarkTheme: boolean): Promise<void> {
    await this.codeEditor.setTheme(isDarkTheme);
  }

  /**
   * 定位当前光标位置
   * @param path 文件路径
   * @param className 类名
   * @param methodName 方法名
   */
  public async positon(path: string, className: string, methodName: string): Promise<void> {
    await this.codeEditor.positon(path, className, methodName);
  }

  handleSwitchBtnClick(): void {
    this.switchView.emit();
  }

}
